import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { auth, googleProvider } from "@/lib/firebase";
import { signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";
import { useIsMobile } from "@/hooks/useMobile";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { trpc } from "@/lib/trpc";
import {
  SquaresFour,
  Kanban as KanbanIcon,
  UsersThree,
  FileText,
  Buildings,
  Receipt,
  Calculator,
  SignOut,
  SidebarSimple,
  PaperPlaneTilt,
  Sparkle,
  UserCheck,
  X,
  Gear,
  ChartLineUp,
  UserList,
  CircleNotch,
  MapPin,
  CaretDown,
} from "@phosphor-icons/react";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { isVideoLivePath } from "@/lib/videoRoomPath";
import { canViewCandidatePipelinePreview } from "@shared/candidatePipelinePreview";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { NotificationBell } from "./NotificationBell";
import { DeadlineAlertBell } from "./DeadlineAlertBell";
import { Button } from "./ui/button";

type MenuItem = {
  icon: any;
  label: string;
  path: string;
  roles?: string[];
  previewOnly?: boolean;
  /** Группа в sidebar (макет Screen 1.2): top — без сабхедера, остальные — uppercase-сабхедер с разделителем. */
  section?: "top" | "recruiting" | "finance";
};

const menuItems: MenuItem[] = [
  { icon: SquaresFour, label: "Дашборд", path: "/", section: "top" },
  {
    icon: KanbanIcon,
    label: "Вакансии",
    path: "/kanban",
    roles: ["owner", "co_owner", "tech_admin", "recruiter", "coordinator"],
    section: "recruiting",
  },
  {
    icon: UserCheck,
    label: "Воронки кандидатов",
    path: "/vacancy-candidates",
    previewOnly: true,
    section: "recruiting",
  },
  {
    icon: UsersThree,
    label: "HR",
    path: "/recruiters",
    roles: ["owner", "co_owner", "tech_admin", "coordinator"],
    section: "recruiting",
  },
  {
    icon: UserList,
    label: "Кандидаты",
    path: "/candidates",
    roles: ["owner", "co_owner", "tech_admin", "recruiter"],
    section: "recruiting",
  },
  { icon: FileText, label: "Договоры", path: "/contracts", section: "finance" },
  {
    icon: Buildings,
    label: "Компании",
    path: "/companies",
    section: "finance",
  },
  {
    icon: Receipt,
    label: "Дебиторка",
    path: "/finance",
    roles: ["owner", "co_owner", "tech_admin", "sales_manager", "coordinator"],
    section: "finance",
  },
  {
    icon: Calculator,
    label: "Расчёты",
    path: "/commissions",
    roles: ["owner", "co_owner", "tech_admin", "sales_manager"],
    section: "finance",
  },
  {
    icon: Gear,
    label: "Настройки",
    path: "/settings",
    roles: ["owner", "co_owner", "tech_admin"],
    section: "top",
  },
];

const SECTION_LABELS: Record<string, string> = {
  recruiting: "Рекрутинг",
  finance: "Финансы",
};
const SECTION_ORDER: MenuItem["section"][] = ["top", "recruiting", "finance"];

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 160;
const MAX_WIDTH = 320;

const REDIRECT_KEY = "auth_redirect";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user, firebaseUser, logout, error: authError } = useAuth();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Save current path when showing login screen (so we can redirect after login)
  useEffect(() => {
    if (!loading && !firebaseUser) {
      const path = window.location.pathname + window.location.search;
      if (path && path !== "/") {
        sessionStorage.setItem(REDIRECT_KEY, path);
      }
    }
  }, [loading, firebaseUser]);

  // After successful login, redirect to saved path
  useEffect(() => {
    if (user) {
      const redirect = sessionStorage.getItem(REDIRECT_KEY);
      if (redirect) {
        sessionStorage.removeItem(REDIRECT_KEY);
        setLocation(redirect);
      }
    }
  }, [user, setLocation]);

  if (loading) return <DashboardLayoutSkeleton />;

  if (!user) {
    /*
      Сервер не ответил — это НЕ отказ в доступе.

      До 19.09.2026 любой неотвеченный запрос приводил человека на экран «Нет
      доступа. Попросите администратора отправить вам ссылку-приглашение». В
      день выкаток рекрутёр попал в минуту перезапуска приложения и решил, что
      его отключили от системы. Сервер при этом честно отвечает
      SERVICE_UNAVAILABLE и называет сбой временным — надо просто не врать
      человеку и дать обновить страницу.

      Отказ в правах (UNAUTHORIZED/FORBIDDEN) сюда не попадает: на нём запрос
      не повторяется и ошибка остаётся, но ниже мы показываем её только когда
      код не про права.
    */
    const codeless = authError?.data?.code;
    const serverDown =
      Boolean(firebaseUser) &&
      Boolean(authError) &&
      codeless !== "UNAUTHORIZED" &&
      codeless !== "FORBIDDEN";
    if (serverDown) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
          <div className="flex flex-col items-center gap-8 p-10 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-200">
                <ChartLineUp className="w-8 h-8 text-white" weight="light" />
              </div>
              <div className="text-center">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  Сервер не отвечает
                </h1>
                <p className="text-sm text-slate-500 mt-2">
                  Доступ у вас есть — не отвечает сам сервер.
                  <br />
                  Обычно это занимает меньше минуты: обновите страницу.
                </p>
              </div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              className="h-10 bg-slate-900 hover:bg-slate-800 text-white"
            >
              Обновить страницу
            </Button>
          </div>
        </div>
      );
    }
    // Firebase logged in but no DB user → access denied
    if (firebaseUser) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
          <div className="flex flex-col items-center gap-8 p-10 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-200">
                  <ChartLineUp className="w-8 h-8 text-white" weight="light" />
                </div>
              </div>
              <div className="text-center">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  Нет доступа
                </h1>
                <p className="text-sm text-slate-500 mt-2">
                  У вас нет доступа к системе.
                  <br />
                  Попросите администратора отправить вам ссылку-приглашение.
                </p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="h-10 border-slate-200 hover:bg-slate-50 text-slate-700"
            >
              <SignOut className="w-4 h-4 mr-2" weight="light" />
              Выйти
            </Button>
            <p className="text-[11px] text-slate-300">
              CHANGE Recruitment Agency
            </p>
          </div>
        </div>
      );
    }

    // Not logged in → login screen
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <div className="flex flex-col items-center gap-8 p-10 max-w-sm w-full mx-4">
          {/* Logo */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-200">
                <ChartLineUp className="w-8 h-8 text-white" weight="light" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-400 border-2 border-white flex items-center justify-center">
                <Sparkle className="w-2.5 h-2.5 text-white" weight="fill" />
              </div>
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Recruit CRM
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Система управления рекрутментом
              </p>
            </div>
          </div>

          {/* Login card */}
          <div className="w-full bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] p-6 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-700">
                Добро пожаловать
              </p>
              <p className="text-xs text-slate-400">
                Войдите, чтобы продолжить работу
              </p>
            </div>
            <Button
              onClick={() => {
                signInWithPopup(auth, googleProvider)
                  .then(r => console.log("[Auth] popup success:", r.user.email))
                  .catch(e => {
                    console.error("[Auth] popup error:", e);
                    if (
                      e?.code !== "auth/popup-closed-by-user" &&
                      e?.code !== "auth/cancelled-popup-request"
                    ) {
                      alert("Ошибка входа через Google: " + (e?.message || e));
                    }
                  });
              }}
              variant="outline"
              className="w-full h-10 border-slate-200 hover:bg-slate-50 text-slate-700 shadow-sm"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Войти через Google
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-[10px] uppercase tracking-wider text-slate-400">
                  или
                </span>
              </div>
            </div>

            <EmailPasswordLoginForm />
          </div>

          <p className="text-[11px] text-slate-300">
            CHANGE Recruitment Agency
          </p>
        </div>
      </div>
    );
  }

  /*
    Живая встреча занимает весь экран, без меню и шапки CRM.

    Вход проверен выше — человек тот же сотрудник, просто сейчас он не ищет
    разделы, а смотрит в лицо собеседнику. Меню сбоку и шапка съедали треть
    ширины, из-за чего на ноутбуке лицо выходило размером с почтовую марку, и
    это первое, что заметила владелица на живой встрече 19.09.2026. Список
    интервью (/video-interviews) сюда не входит — он обычный экран.
  */
  if (isVideoLivePath(location)) {
    return <div className="min-h-svh bg-[#0b0b0f]">{children}</div>;
  }

  return (
    <SidebarProvider
      defaultOpen={false}
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
          // Запас по ширине свёрнутой колонки, чтобы иконка size-9 не лезла
          // в края и стояла центрированно.
          "--sidebar-width-icon": "3.5rem",
        } as CSSProperties
      }
      // Фон родителя = серый фон страницы (тот же что у main через
      // SidebarInset). Sidebar floating-обвёртка лежит на сером как
      // белая карточка с тенью — в p-2 зазоре между ней и краем экрана
      // продолжается серый фон страницы, без белых «обрывов».
      className="bg-[#f5f5f7]"
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

function EmailPasswordLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err: any) {
      const code = err?.code as string | undefined;
      let msg = "Неверный email или пароль";
      if (code === "auth/invalid-email") msg = "Неверный формат email";
      else if (code === "auth/user-disabled") msg = "Аккаунт отключён";
      else if (code === "auth/operation-not-allowed")
        msg = "Email/пароль не включён в Firebase Console";
      else if (code === "auth/too-many-requests")
        msg = "Слишком много попыток, подожди";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Email"
        autoComplete="email"
        required
        disabled={busy}
        className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
      />
      <input
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Пароль"
        autoComplete="current-password"
        required
        disabled={busy}
        className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
      />
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      <Button
        type="submit"
        disabled={busy || !email || !password}
        className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white shadow-sm"
      >
        {busy ? "Вход..." : "Войти по почте"}
      </Button>
    </form>
  );
}

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
}) {
  const { user, logout, refresh } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isHrPage = location === "/recruiters";
  const isCandidatePipeline =
    location === "/vacancy-candidates" ||
    /^\/vacancies\/\d+\/candidates$/.test(location);
  const hasOwnPageHeader =
    isHrPage ||
    (!isMobile && isCandidatePipeline) ||
    location === "/candidates" ||
    location.startsWith("/candidates/") ||
    location === "/video-interviews" ||
    location.startsWith("/video-interviews/");
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isAdmin =
    user?.role === "owner" ||
    user?.role === "co_owner" ||
    user?.role === "tech_admin";
  const isOwner =
    user?.role === "owner" ||
    user?.role === "co_owner" ||
    user?.role === "tech_admin";
  const impersonating = !!(user as any)?.impersonatedBy;
  const [activeBranch, setActiveBranch] = useState(
    () => localStorage.getItem("branch-id") || ""
  );
  // Доп. филиалы рекрутера: JSON-массив id из users.extraBranchIds
  const extraBranchIds: number[] = (() => {
    const raw = (user as any)?.extraBranchIds as string | null | undefined;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        return parsed
          .map((v: any) => Number(v))
          .filter((v: number) => Number.isFinite(v) && v > 0);
    } catch {
      /* ignore */
    }
    return [];
  })();
  // Второй филиал (Украина) закрыт 16.09.2026 — переключатель филиалов скрыт,
  // и владелец больше не попадает в режим «все филиалы», где в поиске и отчётах
  // подмешивались украинские данные. Данные филиала в базе сохранены: чтобы
  // вернуть переключатель, достаточно поставить здесь true.
  const BRANCH_SWITCHER_ENABLED = false;
  const showBranchSwitcher =
    BRANCH_SWITCHER_ENABLED && (isOwner || extraBranchIds.length > 0);
  const branchesQuery = trpc.branches?.list?.useQuery(undefined, {
    enabled: showBranchSwitcher,
    // Филиалы добавляются раз в полгода — держим в кеше час, без рефетча.
    staleTime: 60 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const allBranches = (branchesQuery as any)?.data ?? [];
  // Для рекрутера с extraBranchIds — фильтруем список до разрешённых
  const branches = isOwner
    ? allBranches
    : allBranches.filter((b: any) => {
        const allowed = new Set<number>([
          (user as any)?.branchId ?? 1,
          ...extraBranchIds,
        ]);
        return allowed.has(b.id);
      });
  const realAdmin = (user as any)?.impersonatedBy as
    | { id: number; name: string }
    | undefined;

  // Без выбранного филиала сервер отдаёт владельцу ВСЕ филиалы, включая
  // закрытый украинский. Пока переключателя нет — сеем свой филиал сразу
  // (раньше это делалось только на телефоне).
  useEffect(() => {
    // Список филиалов при скрытом переключателе не загружается, поэтому
    // опираемся на филиал самого пользователя, а не на branches[0].
    if (
      (isMobile || !BRANCH_SWITCHER_ENABLED) &&
      (user?.role === "owner" || user?.role === "tech_admin") &&
      localStorage.getItem("branch-id") !== String(user?.branchId ?? "1")
    ) {
      localStorage.setItem("branch-id", String(user?.branchId ?? "1"));
      window.location.reload();
    }
  }, [isMobile, user?.role, user?.branchId]);

  const visibleItems = menuItems.filter(item => {
    if (item.previewOnly) return canViewCandidatePipelinePreview(user);
    // Флаг waAccess открывает раздел WhatsApp независимо от роли (рекрутер Юля → чаты Vyka).
    if (item.path === "/whatsapp" && (user as any)?.waAccess) return true;
    return !item.roles || (user?.role && item.roles.includes(user.role));
  });
  const activeMenuItem = visibleItems.find(
    item =>
      item.path === location ||
      (item.path === "/vacancy-candidates" &&
        /^\/vacancies\/\d+\/candidates$/.test(location)) ||
      (item.path === "/candidates" && location.startsWith("/candidates/"))
  );

  useEffect(() => {
    if (isCollapsed) setIsResizing(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH)
        setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  const ROLE_LABELS: Record<string, string> = {
    owner: "Владелец",
    co_owner: "Совладелец",
    tech_admin: "Техадмин",
    sales_manager: "МОП",
    coordinator: "Координатор",
    recruiter: "Рекрутер",
  };
  const roleLabel = ROLE_LABELS[user?.role ?? ""] ?? user?.role ?? "";

  const ROLE_COLORS: Record<string, string> = {
    owner: "bg-slate-100 text-slate-700",
    co_owner: "bg-slate-100 text-slate-700",
    tech_admin: "bg-slate-100 text-slate-700",
    sales_manager: "bg-indigo-100 text-indigo-700",
    coordinator: "bg-emerald-100 text-emerald-700",
    recruiter: "bg-teal-100 text-teal-700",
  };
  const roleColor =
    ROLE_COLORS[user?.role ?? ""] ?? "bg-slate-100 text-slate-700";

  const initials = (user?.name ?? "U")
    .split(" ")
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const mainRef = useRef<HTMLElement>(null);
  const { pullDistance, isRefreshing } = usePullToRefresh(mainRef, () => {
    window.location.reload();
  });

  return (
    <>
      {/* Декоративная белая полоса-«монобровь» позади всего: тянется во всю
          ширину viewport на высоте контентного хедера (h-12). z-0 ставит её
          ПОД floating-sidebar (z-10) и под main контентом, поэтому видна
          только в зазоре слева от sidebar — продолжает белую шапку влево
          до края экрана. pointer-events-none — не ловит клики. */}
      {!isMobile && !hasOwnPageHeader && (
        <div className="hidden md:block fixed top-0 left-0 right-0 h-12 bg-white border-b border-neutral-200 z-0 pointer-events-none" />
      )}
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          variant="floating"
          // ВАЖНО: класс пропадает на sidebar-container (внешняя fixed-обёртка).
          // Если задать ему bg — этот фон видно в p-2 зазоре floating-варианта.
          // Делаем bg прозрачным, чтобы через зазор просвечивал серый
          // фон SidebarProvider. Сам белый цвет карточки sidebar даёт уже
          // sidebar-inner через дефолтный bg-sidebar (= --sidebar).
          className="border-0 bg-transparent"
          disableTransition={isResizing}
        >
          {/* Header — клик по чёрному лого с UsersThree сворачивает/разворачивает
              sidebar. Отдельной кнопки SidebarSimple больше нет. */}
          <SidebarHeader className="justify-center !p-2">
            <div
              className={`flex w-full items-center gap-3 ${isCollapsed && !isMobile ? "justify-center" : ""}`}
            >
              <button
                onClick={toggleSidebar}
                title="Свернуть / развернуть"
                // size-8 совпадает с дефолтным размером SidebarMenuButton в
                // collapsed (group-data-[collapsible=icon]:!size-8) — лого и
                // nav-иконки выглядят одинаковыми и стоят на одной оси.
                className="size-8 shrink-0 bg-[oklch(0.205_0_0)] rounded-lg flex justify-center items-center hover:bg-[oklch(0.3_0_0)] transition-colors focus:outline-none"
              >
                <UsersThree
                  className="size-4 text-[oklch(0.985_0_0)]"
                  weight="regular"
                />
              </button>
              {(!isCollapsed || isMobile) && (
                <div className="flex flex-col min-w-0">
                  <span className="font-bold text-neutral-950 text-sm leading-5 truncate">
                    Recruit CRM
                  </span>
                  <span className="text-neutral-500 text-xs leading-4 truncate">
                    CHANGE Agency
                  </span>
                </div>
              )}
            </div>
          </SidebarHeader>

          {/* Nav — группы по section с uppercase-сабхедерами и разделителями */}
          <SidebarContent className="gap-0 py-3">
            {(() => {
              // Top-пункты разделяем на «верхние» (Дашборд, идёт первым) и
              // «нижние» (Настройки в самом низу). Граница — первый
              // не-top пункт в visibleItems.
              const firstGroupedIdx = visibleItems.findIndex(
                i => i.section && i.section !== "top"
              );
              const topStart =
                firstGroupedIdx === -1
                  ? visibleItems.filter(i => i.section === "top")
                  : visibleItems
                      .slice(0, firstGroupedIdx)
                      .filter(i => i.section === "top");
              const topEnd =
                firstGroupedIdx === -1
                  ? []
                  : visibleItems
                      .slice(firstGroupedIdx)
                      .filter(i => i.section === "top");

              const renderItem = (item: MenuItem) => {
                const isActive =
                  location === item.path ||
                  (item.path === "/vacancy-candidates" &&
                    /^\/vacancies\/\d+\/candidates$/.test(location)) ||
                  (item.path === "/candidates" &&
                    location.startsWith("/candidates/"));
                return (
                  <SidebarMenuItem
                    key={item.path}
                    // В collapsed li — flex с принудительным w-full, иначе
                    // li становится fit-content (32px = ширина button) и
                    // вместе с button прижимается к левому краю ul.
                    className={
                      isCollapsed && !isMobile
                        ? "flex w-full justify-center"
                        : ""
                    }
                  >
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      // В collapsed размер и центровку отдаём shadcn-овскому
                      // дефолту (group-data-[collapsible=icon]:!size-8 + !p-2),
                      // мои стили — только для expanded.
                      className={`rounded-lg transition-colors text-sm leading-5 ${
                        isCollapsed && !isMobile
                          ? ""
                          : "h-auto py-1.5 px-3 gap-3"
                      } ${
                        isActive
                          ? "!bg-[oklch(0.97_0_0)] !text-[oklch(0.205_0_0)] font-medium"
                          : "text-[oklch(0.556_0_0)] hover:bg-[oklch(0.97_0_0)] hover:text-[oklch(0.205_0_0)] font-medium"
                      }`}
                    >
                      <item.icon className="size-4 shrink-0" weight="regular" />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              };

              const renderSection = (key: "recruiting" | "finance") => {
                const items = visibleItems.filter(i => i.section === key);
                if (items.length === 0) return null;
                return (
                  <div key={key}>
                    {(!isCollapsed || (isHrPage && isMobile)) && (
                      <span className="block text-[oklch(0.65_0_0)] font-semibold uppercase text-[10px] tracking-widest px-3 py-1 mt-1">
                        {SECTION_LABELS[key]}
                      </span>
                    )}
                    <SidebarMenu
                      className={`gap-0.5 ${isCollapsed && !isMobile ? "px-0" : "px-3"}`}
                    >
                      {items.map(renderItem)}
                    </SidebarMenu>
                  </div>
                );
              };

              const Separator = () => (
                <div
                  className={`border-t border-neutral-200 my-2 ${
                    isCollapsed && !isMobile ? "mx-2" : "mx-3"
                  }`}
                />
              );

              return (
                <>
                  {topStart.length > 0 && (
                    <SidebarMenu
                      className={`gap-0.5 ${isCollapsed && !isMobile ? "px-0" : "px-3"}`}
                    >
                      {topStart.map(renderItem)}
                    </SidebarMenu>
                  )}
                  {topStart.length > 0 && <Separator />}
                  {renderSection("recruiting")}
                  {(visibleItems.some(i => i.section === "recruiting") ||
                    visibleItems.some(i => i.section === "finance")) && (
                    <Separator />
                  )}
                  {renderSection("finance")}
                  {topEnd.length > 0 && <Separator />}
                  {topEnd.length > 0 && (
                    <SidebarMenu
                      className={`gap-0.5 ${isCollapsed && !isMobile ? "px-0" : "px-3"}`}
                    >
                      {topEnd.map(renderItem)}
                    </SidebarMenu>
                  )}
                </>
              );
            })()}
          </SidebarContent>

          {/* Footer — макет Screen 1.2: круглая нейтральная аватарка + имя/роль + LogOut */}
          <SidebarFooter className="p-0 border-t border-neutral-200">
            <div
              className={`flex w-full items-center gap-3 ${
                isCollapsed && !isMobile ? "p-1 justify-center" : "p-4"
              }`}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-3 text-left focus:outline-none rounded-lg hover:bg-neutral-100 transition-colors ${
                      isCollapsed && !isMobile
                        ? "p-1"
                        : "flex-1 min-w-0 -mx-1 px-1 py-1"
                    }`}
                  >
                    <Avatar
                      className={`${isCollapsed && !isMobile ? "size-8" : "size-8"} shrink-0`}
                    >
                      {user?.picture && (
                        <AvatarImage
                          src={user.picture}
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <AvatarFallback className="text-xs font-semibold bg-[oklch(0.97_0_0)] text-[oklch(0.556_0_0)]">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    {(!isCollapsed || (isHrPage && isMobile)) && (
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate font-semibold text-neutral-950 text-xs leading-4">
                          {user?.name || "Пользователь"}
                        </span>
                        <span className="truncate text-neutral-500 text-xs leading-4">
                          {roleLabel}
                        </span>
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-52 rounded-xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12)] border-0"
                >
                  <DropdownMenuItem
                    onClick={() => setLocation("/profile")}
                    className="cursor-pointer rounded-lg mx-1 mt-1"
                  >
                    <div className="flex items-center gap-2.5 py-0.5">
                      <Avatar className="h-8 w-8 shrink-0">
                        {user?.picture && (
                          <AvatarImage
                            src={user.picture}
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <AvatarFallback className="text-xs font-bold bg-gradient-to-br from-slate-700 to-slate-900 text-white">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {user?.displayName || user?.name}
                        </p>
                        <p className="text-xs text-slate-400 truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setLocation("/telegram")}
                    className="cursor-pointer rounded-lg mx-1"
                  >
                    <PaperPlaneTilt
                      className="mr-2 h-3.5 w-3.5"
                      weight="light"
                    />
                    <span>Telegram</span>
                  </DropdownMenuItem>
                  {(isAdmin || impersonating) && (
                    <DropdownMenuItem
                      onClick={() => {
                        if (impersonating) {
                          sessionStorage.removeItem("impersonate-user-id");
                          window.location.reload();
                        } else {
                          setImpersonateOpen(true);
                        }
                      }}
                      className={`cursor-pointer rounded-lg mx-1 ${impersonating ? "text-amber-600 focus:text-amber-600 focus:bg-amber-50" : ""}`}
                    >
                      <UserCheck className="mr-2 h-3.5 w-3.5" weight="light" />
                      <span>
                        {impersonating
                          ? "Выйти из имперсонации"
                          : "Войти как..."}
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    className="cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-50 rounded-lg mx-1 mb-1"
                  >
                    <SignOut className="mr-2 h-3.5 w-3.5" weight="light" />
                    <span>Выйти</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {(!isCollapsed || (isHrPage && isMobile)) && (
                <button
                  onClick={logout}
                  title="Выйти"
                  className="size-8 shrink-0 flex items-center justify-center rounded-lg text-[oklch(0.556_0_0)] hover:text-[oklch(0.205_0_0)] hover:bg-neutral-100 transition-colors"
                >
                  <SignOut className="size-4" weight="regular" />
                </button>
              )}
            </div>
          </SidebarFooter>
        </Sidebar>

        {/* Resize handle */}
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-slate-300/50 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (!isCollapsed) setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset className="bg-[#f5f5f7] h-svh overflow-hidden">
        {!hasOwnPageHeader &&
          (isMobile ? (
            <div className="mx-2 mt-2 flex items-center justify-between bg-white/60 backdrop-blur-xl px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))] safe-x sticky top-0 z-40 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_1px_3px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.02)] rounded-[14px]">
              <div className="flex items-center gap-3">
                <SidebarTrigger className="h-9 w-9 rounded-lg" />
                <span className="font-bold text-[#1C2030] text-base tracking-[-0.02em]">
                  {activeMenuItem?.label ?? "Меню"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {showBranchSwitcher && branches.length > 1 && (
                  <select
                    value={
                      activeBranch ||
                      String((user as any)?.branchId ?? branches[0]?.id ?? "1")
                    }
                    onChange={e => {
                      const val = e.target.value;
                      localStorage.setItem("branch-id", val);
                      setActiveBranch(val);
                      window.location.reload();
                    }}
                    className="text-[10px] h-6 px-1.5 rounded-md border border-slate-200 bg-white/80 text-slate-600 focus:outline-none"
                  >
                    {branches.map((b: any) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                )}
                <DeadlineAlertBell />
                <NotificationBell />
              </div>
            </div>
          ) : (
            // Хедер — макет Screen 1.3: bg-white + border-b, h-12, без shadow.
            // sticky top-0 — стоит на месте в main, белая «продолжающаяся
            // влево» полоса идёт отдельным декоративным div'ом (см. ниже).
            <div className="flex h-12 items-center justify-between bg-white border-b border-neutral-200 px-6 sticky top-0 z-40">
              <div className="flex items-center gap-3 min-w-0">
                {location !== "/settings" && (
                  <span className="font-medium text-neutral-950 text-sm leading-5 truncate">
                    {activeMenuItem?.label ?? ""}
                  </span>
                )}
                <div
                  id="page-header-slot"
                  className="flex items-center gap-3"
                />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <div
                  id="page-header-actions"
                  className="flex items-center gap-2"
                />
                {/* Группа уведомлений с правым разделителем как в макете */}
                <div className="flex items-center gap-1 mr-1 pr-3 border-r border-neutral-200">
                  <DeadlineAlertBell />
                  <NotificationBell />
                </div>
                {/* Branch switcher — обёрнутый select под нативный стиль макета */}
                {showBranchSwitcher && branches.length > 1 && (
                  <div className="relative">
                    <select
                      value={
                        isOwner
                          ? activeBranch
                          : activeBranch ||
                            String(
                              (user as any)?.branchId ?? branches[0]?.id ?? "1"
                            )
                      }
                      onChange={e => {
                        const val = e.target.value;
                        if (val) {
                          localStorage.setItem("branch-id", val);
                        } else {
                          localStorage.removeItem("branch-id");
                        }
                        setActiveBranch(val);
                        window.location.reload();
                      }}
                      className="appearance-none transition-colors font-medium rounded-lg text-neutral-950 text-sm leading-5 border border-neutral-200 bg-white pl-8 pr-8 h-8 hover:bg-neutral-50 focus:outline-none focus:border-neutral-300 cursor-pointer"
                    >
                      {isOwner && <option value="">Все филиалы</option>}
                      {branches.map((b: any) => (
                        <option key={b.id} value={String(b.id)}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <MapPin className="size-3.5 text-neutral-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <CaretDown className="size-3.5 text-neutral-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                )}
              </div>
            </div>
          ))}
        {impersonating && (
          <div className="mx-3 sm:mx-5 lg:mx-7 mt-2 flex items-center justify-between bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl text-sm">
            <span className="text-amber-800">
              <UserCheck
                className="inline h-4 w-4 mr-1.5 -mt-0.5"
                weight="light"
              />
              Вы вошли как <b>{user?.displayName || user?.name}</b> (от имени{" "}
              {realAdmin?.name})
            </span>
            <button
              onClick={() => {
                sessionStorage.removeItem("impersonate-user-id");
                window.location.reload();
              }}
              className="text-amber-600 hover:text-amber-800 ml-3"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <main
          ref={mainRef}
          className="relative flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 lg:px-8 safe-x pt-3"
        >
          {(pullDistance > 0 || isRefreshing) && (
            <div
              className="pointer-events-none absolute left-0 right-0 top-0 z-50 flex items-start justify-center overflow-hidden"
              style={{
                height: isRefreshing ? 56 : pullDistance,
                transition: isRefreshing ? "height 150ms ease-out" : "none",
              }}
            >
              <div
                className="mt-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06),0_4px_12px_rgba(0,0,0,0.08)] backdrop-blur"
                style={{
                  opacity: isRefreshing ? 1 : Math.min(pullDistance / 80, 1),
                  transform: isRefreshing
                    ? "none"
                    : `rotate(${Math.min(pullDistance / 80, 1) * 360}deg)`,
                  transition: isRefreshing ? "opacity 150ms" : "none",
                }}
              >
                <CircleNotch
                  className={`h-4 w-4 text-slate-500 ${isRefreshing ? "animate-spin" : ""}`}
                  weight="bold"
                />
              </div>
            </div>
          )}
          {children}
        </main>
      </SidebarInset>
      {impersonateOpen && (
        <ImpersonateDialog onClose={() => setImpersonateOpen(false)} />
      )}
    </>
  );
}

function ImpersonateDialog({ onClose }: { onClose: () => void }) {
  const usersQuery = trpc.users.list.useQuery();
  const users = usersQuery.data ?? [];

  const ROLE_LABELS: Record<string, string> = {
    owner: "Владелец",
    co_owner: "Совладелец",
    tech_admin: "Техадмин",
    sales_manager: "МОП",
    coordinator: "Координатор",
    recruiter: "Рекрутер",
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-80 max-h-[70vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm text-slate-800">
            Войти как пользователь
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[calc(70vh-48px)] p-2">
          {users.map((u: any) => (
            <button
              key={u.id}
              onClick={() => {
                sessionStorage.setItem("impersonate-user-id", String(u.id));
                window.location.reload();
              }}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-3 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                {(u.displayName || u.name || "?")
                  .split(" ")
                  .map((w: any) => w[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {u.displayName || u.name}
                </p>
                <p className="text-[11px] text-slate-400">
                  {ROLE_LABELS[u.role] ?? u.role}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
